/**
 * Como a sessão de atendimento se apresenta ao PARCEIRO.
 *
 * **O que motivou (06/08/2026).** Medido no navegador, o cabeçalho da conversa
 * dizia `Sessão 863c90ed…` — um pedaço de UUID. Não diz para onde a mensagem
 * foi nem quem vai responder. O Augusto pediu setor e atendente.
 *
 * Está num módulo próprio, e não dentro do JSX, porque a regra tem casos que a
 * frase combinada não cobria — sessão **encerrada e nunca atendida** não pode
 * dizer "aguardando atendente", e dado antigo não tem setor nenhum. Regra com
 * casos assim precisa de teste, e teste precisa que ela seja alcançável.
 */
import type { CustomerSession, SessionStatus } from "@/types/atendimento";

/** Sessões que já acabaram — nelas "aguardando" seria mentira. */
const STATUS_TERMINAIS: ReadonlySet<SessionStatus> = new Set<SessionStatus>([
  "RESOLVED",
  "CANCELLED",
]);

/**
 * Prefixo de código que alguns cadastros embutem no NOME do setor.
 *
 * Medido no banco: `codigo = "FIN_FINANCEIRO"`, `nome = "[FIN] FINANCEIRO"`.
 * O código aparece duas vezes, e no cabeçalho de 412px do celular isso rouba
 * espaço de quem importa — o nome do setor e o atendente.
 *
 * Só remove o que casa com `[LETRAS/DÍGITOS] ` no INÍCIO: um nome legítimo com
 * colchetes no meio passa intacto.
 */
const PREFIXO_DE_CODIGO = /^\[[A-Z0-9_-]{1,10}\]\s*/i;

/**
 * Título da sessão: nome do setor, sem o código repetido.
 *
 * Sem setor (sessão anterior a 06/08/2026, ou resposta de endpoint que não
 * carrega o relacionamento), cai no formato antigo em vez de mostrar vazio —
 * um cabeçalho em branco seria pior que um UUID.
 */
export function tituloDaSessao(s: Pick<CustomerSession, "id" | "sector_name">): string {
  const setor = (s.sector_name || "").replace(PREFIXO_DE_CODIGO, "").trim();
  return setor || `Sessão ${s.id.slice(0, 8)}…`;
}

/**
 * Quem está atendendo, ou `null` quando não há nada honesto a dizer.
 *
 * - com atendente → o primeiro nome, como veio do servidor
 * - na fila       → "aguardando atendente"
 * - encerrada sem atendente → `null` (a sessão acabou; ninguém está esperando)
 */
export function atendenteDaSessao(
  s: Pick<CustomerSession, "status" | "attendant_first_name">
): string | null {
  const nome = (s.attendant_first_name || "").trim();
  if (nome) return nome;
  if (STATUS_TERMINAIS.has(s.status)) return null;
  return "aguardando atendente";
}

/**
 * A linha inteira: `"FINANCEIRO · João"`, `"FINANCEIRO · aguardando atendente"`
 * ou só `"FINANCEIRO"` quando não há o que dizer sobre atendente.
 *
 * O separador é `·` e não `-` porque o nome do setor pode conter hífen
 * ("PÓS-VENDA"), e aí a leitura fica ambígua.
 */
export function descricaoDaSessao(
  s: Pick<CustomerSession, "id" | "sector_name" | "status" | "attendant_first_name">
): string {
  const atendente = atendenteDaSessao(s);
  const titulo = tituloDaSessao(s);
  return atendente ? `${titulo} · ${atendente}` : titulo;
}

/** A marca da conversa só leitura, sozinha: a lista a mostra à parte para dar o espaço da linha de cima ao nome. */
export const MARCA_SO_LEITURA = "só leitura";

/**
 * Título da conversa que a CONTA OFICIAL da loja vê só em leitura (atendimento ativo, 01/11/2026): o SE abriu a conversa com um
 * colaborador dela, e ela não escreve ali. O nome é o do colaborador, não o do setor — é de quem a conversa é.
 *
 * Nome nulo ou em branco (o servidor não achou o nome) cai na palavra `colaborador`: a frase fixa nunca sai com `null` nem com
 * dois espaços no meio.
 */
export function tituloDaConversaSoLeitura(s: Pick<CustomerSession, "colaborador_nome">): string {
  return `${tituloCurtoDaConversaSoLeitura(s)} · ${MARCA_SO_LEITURA}`;
}

/**
 * `Conversa com {nome}`: o título SEM o `· só leitura`. É o que a lista "Minhas sessões" mostra na 1ª linha (a 1366 px a badge
 * de status deixa uns 130 px para o título, e o título inteiro cortava em "Conversa com…", sem o nome — D2 do E2E de 02/11/2026).
 * Mesma regra do nome do `tituloDaConversaSoLeitura`: nulo ou em branco vira `colaborador`.
 */
export function tituloCurtoDaConversaSoLeitura(
  s: Pick<CustomerSession, "colaborador_nome">
): string {
  const nome = (s.colaborador_nome || "").trim();
  return `Conversa com ${nome || "colaborador"}`;
}
